import { useState } from 'react';
import { useQuery } from 'react-query';
import { AuditEntry, getAuditLog } from '../../../network/audit';
import { usePeopleOptions } from '../../../hooks/usePeopleOptions';
import { Pagination } from '../../../ui/component/pagination';
import { apiErrorMessage } from '../../../utils/notifyError';

const PAGE_SIZE = 25;

type Filters = { action: string; actor: string; from: string; to: string };

const EMPTY_FILTERS: Filters = { action: '', actor: '', from: '', to: '' };

/** `person.role_change` -> `Person role change`. */
export function formatAction(action: string) {
  const text = action.replace(/[._]/g, ' ').trim();
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function formatMetadata(metadata: Record<string, unknown> | null | undefined) {
  if (!metadata) return '';
  return Object.entries(metadata)
    .filter(([, value]) => value !== undefined && value !== null && value !== '')
    .map(
      ([key, value]) =>
        `${key}: ${typeof value === 'object' ? JSON.stringify(value) : String(value)}`,
    )
    .join(', ');
}

function actorLabel(entry: AuditEntry) {
  return entry.actor?.name || entry.actor?.email || entry.actor?.id || 'System';
}

const selectClass =
  'rounded-md border border-gray-300 px-2 py-1 text-sm text-gray-900';

/** Settings tab listing the company's audit log, newest first. */
export function AuditLog() {
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const { people } = usePeopleOptions();

  const { data, isLoading, isError, error } = useQuery(
    ['audit-log', page, filters],
    () => getAuditLog({ page, limit: PAGE_SIZE, ...filters }),
    { keepPreviousData: true },
  );

  const update = (patch: Partial<Filters>) => {
    setFilters((current) => ({ ...current, ...patch }));
    setPage(0);
  };

  const entries = data?.data ?? [];
  const actions = data?.actions ?? [];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col text-xs text-gray-600">
          Action
          <select
            aria-label="Action"
            className={selectClass}
            value={filters.action}
            onChange={(event) => update({ action: event.target.value })}
          >
            <option value="">All actions</option>
            {actions.map((action) => (
              <option key={action} value={action}>
                {formatAction(action)}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col text-xs text-gray-600">
          Person
          <select
            aria-label="Person"
            className={selectClass}
            value={filters.actor}
            onChange={(event) => update({ actor: event.target.value })}
          >
            <option value="">Anyone</option>
            {people.map((person) => (
              <option key={person.id} value={person.id}>
                {person.name}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col text-xs text-gray-600">
          From
          <input
            type="date"
            aria-label="From"
            className={selectClass}
            value={filters.from}
            max={filters.to || undefined}
            onChange={(event) => update({ from: event.target.value })}
          />
        </label>
        <label className="flex flex-col text-xs text-gray-600">
          To
          <input
            type="date"
            aria-label="To"
            className={selectClass}
            value={filters.to}
            min={filters.from || undefined}
            onChange={(event) => update({ to: event.target.value })}
          />
        </label>
        <button
          type="button"
          className="rounded-md border border-gray-300 px-3 py-1 text-sm"
          onClick={() => update(EMPTY_FILTERS)}
        >
          Clear
        </button>
      </div>

      {isError && (
        <p className="text-sm text-red-600" role="alert">
          {apiErrorMessage(error, 'Could not load the audit log.')}
        </p>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead>
            <tr className="text-left text-gray-500">
              <th className="py-2 pr-3 font-medium">When</th>
              <th className="py-2 pr-3 font-medium">Who</th>
              <th className="py-2 pr-3 font-medium">Action</th>
              <th className="py-2 pr-3 font-medium">Target</th>
              <th className="py-2 font-medium">Details</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (
              <tr>
                <td colSpan={5} className="py-4 text-center text-gray-500">
                  Loading...
                </td>
              </tr>
            )}
            {!isLoading && entries.length === 0 && !isError && (
              <tr>
                <td colSpan={5} className="py-4 text-center text-gray-500">
                  No audit entries match these filters
                </td>
              </tr>
            )}
            {entries.map((entry) => (
              <tr key={entry.id} className="align-top">
                <td className="whitespace-nowrap py-2 pr-3 text-gray-700">
                  {new Date(entry.created_at).toLocaleString()}
                </td>
                <td className="py-2 pr-3 text-gray-900">{actorLabel(entry)}</td>
                <td className="py-2 pr-3 text-gray-900">
                  {formatAction(entry.action)}
                </td>
                <td className="py-2 pr-3 text-gray-700">
                  {entry.target?.type}
                  {entry.target?.id ? ` ${entry.target.id}` : ''}
                </td>
                <td className="break-all py-2 text-gray-500">
                  {formatMetadata(entry.metadata)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination
        limit={PAGE_SIZE}
        count={data?.count ?? 0}
        page={page}
        onPageChange={setPage}
      />
    </div>
  );
}
